import Link from "next/link";
import { ArrowRight } from "lucide-react";
import type { CaseStudy } from "@/lib/data/case-studies";
import IllustrativeBadge from "./IllustrativeBadge";

/** Outcome first: the headline and the numbers carry the card; the summary supports them. */
export default function CaseStudyCard({ study }: { study: CaseStudy }) {
  return (
    <Link
      href={`/case-studies/${study.slug}`}
      className="group h-full flex flex-col rounded-xl border border-mkt-primary/20 bg-white overflow-hidden shadow-[0_4px_16px_rgba(0,60,185,0.10)] hover:shadow-[0_8px_30px_rgba(0,60,185,0.18)] hover:border-mkt-primary/40 transition-all duration-300"
    >
      <div className="flex flex-wrap items-center justify-between gap-size-sm border-b border-mkt-outline-variant/40 bg-mkt-primary-container/[0.06] px-size-md py-size-sm">
        <span className="font-label-md text-label-md uppercase tracking-widest text-mkt-primary">{study.industry}</span>
        {study.illustrative ? <IllustrativeBadge /> : null}
      </div>

      <div className="flex flex-1 flex-col gap-size-sm p-size-md">
        <span className="font-label-md text-label-md text-mkt-on-surface-variant">{study.category}</span>
        <h3 className="font-headline-md text-headline-sm text-mkt-on-surface leading-snug group-hover:text-mkt-primary transition-colors">
          {study.title}
        </h3>
        <p className="font-body-md text-body-md text-mkt-on-surface-variant">{study.summary}</p>

        <dl className="mt-auto grid grid-cols-3 gap-size-sm border-t border-mkt-outline-variant/40 pt-size-md">
          {study.metrics.map((metric) => (
            <div key={metric.label} className="flex flex-col-reverse gap-1">
              <dt className="text-xs leading-snug text-mkt-on-surface-variant">{metric.label}</dt>
              <dd className="font-headline-md text-headline-sm font-bold text-mkt-primary-container">{metric.value}</dd>
            </div>
          ))}
        </dl>

        <span className="mt-size-sm inline-flex items-center gap-1.5 font-label-md text-label-md font-bold text-mkt-primary">
          Read case study
          <ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}
